import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { PageHeader, Btn, Select, Card, Badge, EmptyState, fmtDate } from "@/components/ui-kit";
import { Calendar, GripVertical, Trash2, Plus, CheckCircle2, Stethoscope, Building2 } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export default function RoutePlanner() {
  const { t } = useLang();
  const today = new Date().toISOString().split("T")[0];
  const [planDate, setPlanDate] = useState(today);
  const [doctors, setDoctors] = useState([]);
  const [pharmacies, setPharmacies] = useState([]);
  const [items, setItems] = useState([]);
  const [planId, setPlanId] = useState(null);
  const [type, setType] = useState("pharmacy");
  const [pickId, setPickId] = useState("");

  const load = async () => {
    const [d, p, plans] = await Promise.all([
      api.get("/doctors"), api.get("/pharmacies"),
      api.get("/route-plans", { params: { plan_date: planDate } }),
    ]);
    setDoctors(d.data); setPharmacies(p.data);
    if (plans.data.length > 0) {
      const plan = plans.data[0];
      setItems(plan.items || []);
      setPlanId(plan.id);
    } else {
      setItems([]); setPlanId(null);
    }
  };
  useEffect(() => { load(); }, [planDate]);

  const addItem = () => {
    if (!pickId) return;
    const list = type === "doctor" ? doctors : pharmacies;
    const target = list.find(o => o.id === pickId);
    if (!target) return;
    setItems([...items, {
      target_type: type,
      target_id: target.id,
      target_name: target.full_name || target.name,
      priority: items.length + 1,
      completed: false,
    }]);
    setPickId("");
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(items);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setItems(reordered.map((it, i) => ({ ...it, priority: i + 1 })));
  };

  const save = async () => {
    const res = await api.post("/route-plans", { plan_date: planDate, items });
    if (res.data.id) setPlanId(res.data.id);
    await load();
  };

  const markDone = async (idx) => {
    if (!planId) await save();
    await api.put(`/route-plans/${planId}/items/${idx}/complete`);
    await load();
  };

  const completed = items.filter(i => i.completed).length;

  return (
    <div data-testid="route-planner-page">
      <PageHeader
        title="Daily Route Planner"
        subtitle="Plan and reorder visits — drag to prioritize"
        actions={<Btn onClick={save} data-testid="save-plan-btn">Save Plan</Btn>}
      />

      <Card className="p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="overline mb-1 block">Plan Date</label>
            <input type="date" value={planDate} onChange={(e) => setPlanDate(e.target.value)} className="rounded ring-1 ring-slate-300 px-3 py-1.5 text-sm"/>
          </div>
          <div className="flex gap-2 items-end">
            <Select label="Type" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="pharmacy">Pharmacy</option><option value="doctor">Doctor</option>
            </Select>
            <Select label="Pick" value={pickId} onChange={(e) => setPickId(e.target.value)}>
              <option value="">--</option>
              {(type === "doctor" ? doctors : pharmacies).map(o => (
                <option key={o.id} value={o.id}>{o.full_name || o.name} {o.city ? `· ${o.city}` : ""}</option>
              ))}
            </Select>
            <Btn type="button" onClick={addItem} disabled={!pickId} data-testid="add-route-item"><Plus className="w-4 h-4"/>Add</Btn>
          </div>
          <div className="ms-auto flex items-center gap-3">
            <Badge color="blue">{items.length} stops</Badge>
            <Badge color="green">{completed} completed</Badge>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        {items.length > 0 ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="route">
              {(prov) => (
                <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-2">
                  {items.map((it, idx) => (
                    <Draggable key={`${it.target_id}-${idx}`} draggableId={`${it.target_id}-${idx}`} index={idx}>
                      {(p2, snap) => (
                        <div ref={p2.innerRef} {...p2.draggableProps} className={`flex items-center gap-3 p-3 rounded-md border ${snap.isDragging ? "shadow-lg bg-blue-50" : "bg-white border-slate-200"} ${it.completed ? "opacity-60" : ""}`}>
                          <div {...p2.dragHandleProps} className="cursor-grab text-slate-400 hover:text-slate-600">
                            <GripVertical className="w-5 h-5"/>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 grid place-items-center font-bold text-sm">{idx + 1}</div>
                          {it.target_type === "doctor"
                            ? <Stethoscope className="w-4 h-4 text-purple-600"/>
                            : <Building2 className="w-4 h-4 text-blue-600"/>}
                          <div className="flex-1">
                            <div className={`font-semibold ${it.completed ? "line-through" : ""}`}>{it.target_name}</div>
                            <div className="text-xs text-slate-500">{it.target_type}</div>
                          </div>
                          {!it.completed && (
                            <button onClick={() => markDone(idx)} className="text-xs text-emerald-700 font-semibold hover:bg-emerald-50 px-2 py-1 rounded inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3"/>Done
                            </button>
                          )}
                          {it.completed && <Badge color="green">Done</Badge>}
                          <button onClick={() => removeItem(idx)} className="p-1 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-600"/></button>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {prov.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <EmptyState message="No stops planned. Add doctors or pharmacies above to build your route."/>
        )}
      </Card>
    </div>
  );
}
